import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { CheckCircle } from "@phosphor-icons/react";

export default function Billing() {
  const { user, refresh } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState("");

  useEffect(() => {
    api.get("/billing/plans").then(({ data }) => setPlans(data));
  }, []);

  const subscribe = async (planId) => {
    setLoading(planId);
    try {
      const { data: order } = await api.post("/billing/order", { plan_id: planId });
      if (order.mock) {
        // Simulate checkout success
        const verify = await api.post("/billing/verify", {
          order_id: order.id,
          payment_id: `pay_mock_${Date.now()}`,
          signature: `mock_${Date.now()}`,
          plan_id: planId,
        });
        toast.success("Subscription activated (mock payment)");
        await refresh();
      } else {
        // Real Razorpay checkout would open here
        if (!window.Razorpay) {
          toast.error("Razorpay SDK not loaded");
          return;
        }
        const options = {
          key: order.key_id,
          amount: order.amount,
          currency: order.currency,
          order_id: order.id,
          name: "Tradelog",
          description: `${order.plan.name} plan`,
          handler: async (res) => {
            await api.post("/billing/verify", {
              order_id: res.razorpay_order_id,
              payment_id: res.razorpay_payment_id,
              signature: res.razorpay_signature,
              plan_id: planId,
            });
            toast.success("Payment verified. Subscription active.");
            await refresh();
          },
          prefill: { name: user?.name, email: user?.email },
          theme: { color: "#3b82f6" },
        };
        new window.Razorpay(options).open();
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail === "payment_provider_auth_failed") {
        toast.error("Payment gateway rejected our API key. Please contact support — the site admin needs to rotate the Razorpay key.");
      } else if (detail === "payment_provider_unavailable") {
        toast.error("Payment gateway is temporarily unavailable. Please try again in a minute.");
      } else {
        toast.error(detail || "Payment failed");
      }
    } finally {
      setLoading("");
    }
  };

  const sub = user?.subscription;
  const isPro = user?.is_pro;
  const onTrial = sub?.status === "trial";
  const paidActive = isPro && !onTrial;

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">Subscription</p>
      <h1 className="font-display text-4xl font-bold tracking-tight mt-1">Billing</h1>
      <p className="text-sm text-muted-foreground mt-1 mb-8">Choose the plan that fits your journey.</p>

      <div className="rounded-xl border border-border bg-card p-6 mb-8">
        <p className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Current status</p>
        {isPro ? (
          <div className="mt-2">
            <p className="font-display text-2xl font-bold text-emerald-400 capitalize">{sub?.plan_id || "Active"}</p>
            <p className="text-sm text-muted-foreground mt-1">
              Renews on {(sub?.current_period_end || "").slice(0,10)}
            </p>
          </div>
        ) : (
          <div className="mt-2">
            <p className="font-display text-2xl font-bold">Free trial</p>
            <p className="text-sm text-muted-foreground mt-1">
              Ends {(user?.trial_ends_at || "").slice(0,10)}. 70% of features lock after trial.
            </p>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {plans.map((p, i) => (
          <div key={p.id} data-testid={`billing-plan-${p.id}`}
            className={`rounded-2xl p-6 ${i === 1 ? "tracing-beam" : "bg-card border border-border"}`}>
            <div className={i === 1 ? "relative rounded-2xl p-6 bg-card" : ""}>
              <h3 className="font-display text-2xl font-bold">{p.name}</h3>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-num text-5xl font-black">₹{(p.amount/100).toLocaleString("en-IN")}</span>
                <span className="text-sm text-muted-foreground">/{p.name.toLowerCase()}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm">
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" />Unlimited trades & accounts</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" />CSV import & chart uploads</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" />Sharable PnL link</li>
                <li className="flex gap-2"><CheckCircle size={16} className="text-emerald-500 mt-0.5" />WhatsApp daily reports</li>
              </ul>
              <Button className="w-full mt-6 rounded-full" onClick={() => subscribe(p.id)}
                disabled={loading === p.id || (isPro && sub?.plan_id === p.id)}
                data-testid={`subscribe-${p.id}`}>
                {isPro && sub?.plan_id === p.id ? "Current plan" : (loading === p.id ? "Processing…" : "Subscribe")}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-6">
        Payments via Razorpay (INR). Use test card 4111 1111 1111 1111 with any future expiry and CVV — no real charge is made.
      </p>
    </div>
  );
}
